// XZ3D shape-blob v1 geometry core — pure functions, no THREE, no DOM.
//
// Implements XZ3D_SHAPE_BLOB_V1_SCHEMA_20260801.md §2 verbatim: the encounter
// interior is a union of axis-aligned boxes on the x/y floor plane (z up); a
// segment may cross a box face only through a declared opening (checked against
// `passable` for movement, `transparent` for sight/AOE) or where the crossing
// point is strictly inside another box (overlap connects, abutment does not).
// This module is the prototype for the eventual Janus/TS port — keep it free of
// rendering concerns so the same rules run server-side unchanged.

import { curtainBinding } from './ft3d-curtain-core.js';
const EPS = 1e-6;

// Runtime movement callers may add one bounded support box around an exterior
// trip. It extends the walkable union, but it is deliberately not structure:
// it contributes no wall faces and cannot erase a real room wall through the
// overlap exemption. The marker is query-only and is never persisted.
export function boxIsNavigationOpenGround(box) {
  const creation = box?.creation ?? {};
  return box?.navigationOpenGround === true
    || creation.enclosure === 'none'
    || creation.environment === 'exterior-open'
    || creation.presetId === 'exterior-open';
}

export function validateShape(shape) {
  const fail = message => { throw new Error(`shape: ${message}`); };
  if (!shape || typeof shape !== 'object' || Array.isArray(shape)) fail('must be an object');
  if (shape.v !== 1) fail(`unsupported version ${shape.v} (reader supports v=1)`);
  if (shape.unit !== 'ft') fail(`unit must be "ft", got ${shape.unit}`);
  if (!Array.isArray(shape.boxes) || shape.boxes.length > 64) {
    fail('boxes must be an array of 0-64 entries');
  }
  const openings = shape.openings ?? [];
  if (!Array.isArray(openings) || openings.length > 128) fail('openings must be an array of 0-128 entries');

  const idPattern = /^[a-z0-9_-]{1,32}$/;
  const checkRange = (range, path) => {
    if (!range || typeof range !== 'object') fail(`${path} must be {min, max}`);
    if (!Number.isFinite(range.min) || !Number.isFinite(range.max)) fail(`${path} must be finite`);
    if (!(range.max > range.min)) fail(`${path}.max must exceed min`);
  };
  // DAFT3D S24 scene walls: absent, or a list of well-formed axis-aligned slabs (PHP twin in ft_shape_validate).
  const sceneWalls = shape.sceneWalls ?? [];
  if (!Array.isArray(sceneWalls) || sceneWalls.length > 256) fail('sceneWalls must be an array of 0-256 entries');
  const wallIds = new Set();
  for (const [index, wall] of sceneWalls.entries()) {
    const path = `sceneWalls[${index}]`;
    if (!wall || typeof wall.id !== 'string' || !/^[A-Za-z0-9_:.-]{1,64}$/.test(wall.id) || wallIds.has(wall.id)) fail(`${path}.id invalid`);
    wallIds.add(wall.id);
    if (wall.axis !== 'x' && wall.axis !== 'y') fail(`${path}.axis invalid`);
    if (!Number.isFinite(wall.at)) fail(`${path}.at must be finite`);
    checkRange(wall.span, `${path}.span`);
    checkRange(wall.z, `${path}.z`);
    if (!Number.isFinite(wall.thicknessFt) || !(wall.thicknessFt > 0) || wall.thicknessFt > 10) fail(`${path}.thicknessFt invalid`);
    if (wall.openings !== undefined && (!Array.isArray(wall.openings) || wall.openings.length > 64)) fail(`${path}.openings must be an array of 0-64 entries`);
    for (const [slot, opening] of (wall.openings ?? []).entries()) {
      if (!opening || typeof opening !== 'object') fail(`${path}.openings[${slot}] invalid`);
      checkRange(opening.span, `${path}.openings[${slot}].span`);
      if (opening.z !== undefined) checkRange(opening.z, `${path}.openings[${slot}].z`);
    }
  }

  const boxIds = new Set();
  for (const [index, box] of shape.boxes.entries()) {
    const path = `boxes[${index}]`;
    // typeof check before the regex: `idPattern.test(1)` coerces to "1" and
    // PASSES, but a numeric id collides with its string twin once a PHP port
    // uses it as an array key (JS Sets keep 1 and "1" apart, PHP keys do not).
    // The schema says id is a string — enforce it on both sides so the two
    // validators can never disagree. (Janus port parity, 2026-08-02.)
    if (typeof box.id !== 'string' || !idPattern.test(box.id)) fail(`${path}.id invalid`);
    if (boxIds.has(box.id)) fail(`${path}.id duplicate "${box.id}"`);
    boxIds.add(box.id);
    checkRange(box.x, `${path}.x`);
    checkRange(box.y, `${path}.y`);
    if (!Number.isFinite(box.floor) || !Number.isFinite(box.ceiling)) fail(`${path} floor/ceiling must be finite`);
    if (!(box.ceiling > box.floor)) fail(`${path}.ceiling must exceed floor`);
    // creation.roofRemoved (2026-08-28): present ⇒ a real boolean (the opening-flag rule) — a string
    // "true" must never read as open-to-sky, nor silently as roofed.
    if (box.creation && typeof box.creation === 'object' && Object.hasOwn(box.creation, 'roofRemoved')
      && typeof box.creation.roofRemoved !== 'boolean') {
      fail(`${path}.creation.roofRemoved must be boolean when present`);
    }
    // creation.lightFixtures (2026-08-28): present ⇒ one of the known modes.
    if (box.creation && typeof box.creation === 'object' && Object.hasOwn(box.creation, 'lightFixtures')
      && !LIGHT_FIXTURE_MODES.includes(box.creation.lightFixtures)) {
      fail(`${path}.creation.lightFixtures must be one of ${LIGHT_FIXTURE_MODES.join('|')} when present`);
    }
    if (box.creation && typeof box.creation === 'object' && Object.hasOwn(box.creation, 'solidMaterial')
      && !['earth', 'stone', 'worked'].includes(box.creation.solidMaterial)) {
      fail(`${path}.creation.solidMaterial must be one of earth|stone|worked when present`);
    }
  }

  const openingIds = new Set();
  for (const [index, opening] of openings.entries()) {
    const path = `openings[${index}]`;
    if (typeof opening.id !== 'string' || !idPattern.test(opening.id)) fail(`${path}.id invalid`);
    if (openingIds.has(opening.id)) fail(`${path}.id duplicate "${opening.id}"`);
    openingIds.add(opening.id);
    if (opening.creation && Object.hasOwn(opening.creation, 'covering')
      && (!curtainBinding(opening) || !shape.boxes.some(b => b.id === opening.creation.covering.roomId))) {
      fail(`${path}.creation.covering requires an independent inventory and owning room`);
    }
    if (!opening.plane || typeof opening.plane !== 'object' || (opening.plane.axis !== 'x' && opening.plane.axis !== 'y')) {
      fail(`${path}.plane.axis must be "x" or "y"`);
    }
    if (!Number.isFinite(opening.plane.at)) fail(`${path}.plane.at must be finite`);
    checkRange(opening.span, `${path}.span`);
    checkRange(opening.z, `${path}.z`);
    // Omission means true (schema default), but a PRESENT value must be a real
    // boolean — a string "false" silently reading as open is the worst kind of
    // authoring bug.
    for (const flag of ['passable', 'transparent', 'passesLight']) {
      if (flag in opening && opening[flag] !== undefined && typeof opening[flag] !== 'boolean') {
        fail(`${path}.${flag} must be boolean when present`);
      }
    }
    // The opening must puncture a real face: its plane sits on a box extent on
    // that axis and its span/z rectangle fits inside that face.
    const onFace = shape.boxes.some(box => {
      const extents = opening.plane.axis === 'x' ? box.x : box.y;
      const spanExtents = opening.plane.axis === 'x' ? box.y : box.x;
      return (Math.abs(opening.plane.at - extents.min) < EPS || Math.abs(opening.plane.at - extents.max) < EPS)
        && opening.span.min >= spanExtents.min - EPS && opening.span.max <= spanExtents.max + EPS
        && opening.z.min >= box.floor - EPS && opening.z.max <= box.ceiling + EPS;
    });
    if (!onFace) fail(`${path} does not lie on any box face`);
  }
  return shape;
}

export function containingBoxes(shape, x, y) {
  return shape.boxes.filter(box =>
    x >= box.x.min - EPS && x <= box.x.max + EPS
    && y >= box.y.min - EPS && y <= box.y.max + EPS);
}

export function insideUnion(shape, x, y) {
  return containingBoxes(shape, x, y).length > 0;
}

// Strict interior on the floor plane, inclusive on the vertical span — the
// overlap exemption must not connect boxes whose heights do not reach the
// query z (a corridor's 8 ft volume cannot exempt a crossing at z = 12).
function strictlyInsideAnyBox(shape, x, y, z) {
  return shape.boxes.some(box =>
    !boxIsNavigationOpenGround(box)
    && x > box.x.min + EPS && x < box.x.max - EPS
    && y > box.y.min + EPS && y < box.y.max - EPS
    && z >= box.floor - EPS && z <= box.ceiling + EPS);
}

// Squeeze = ceiling of the box you stand in; overlap uses the highest
// containing ceiling. Null when outside the union.
export function ceilingAt(shape, x, y) {
  const boxes = containingBoxes(shape, x, y);
  if (!boxes.length) return null;
  const structural = boxes.filter(box => !boxIsNavigationOpenGround(box));
  return Math.max(...(structural.length ? structural : boxes).map(box => box.ceiling));
}

export function floorAt(shape, x, y) {
  const boxes = containingBoxes(shape, x, y);
  if (!boxes.length) return null;
  const structural = boxes.filter(box => !boxIsNavigationOpenGround(box));
  return Math.min(...(structural.length ? structural : boxes).map(box => box.floor));
}

function openingAdmits(opening, axis, at, spanCoord, z, mode) {
  if (opening.plane.axis !== axis || Math.abs(opening.plane.at - at) > EPS) return false;
  if (spanCoord < opening.span.min - EPS || spanCoord > opening.span.max + EPS) return false;
  if (z < opening.z.min - EPS || z > opening.z.max + EPS) return false;
  return mode === 'move' ? opening.passable !== false
    : opening.transparent !== false && opening.coveringState?.blocksSight !== true;
}

// First blocking wall hit along the floor-plane segment (x1,y1)->(x2,y2) at
// standing/ray height z, or null when the segment is clear.
//   mode 'move'  — openings must be passable AND the point just past the
//                  crossing must still be inside the union (you cannot walk
//                  out a window into the void).
//   mode 'sight' — openings must be transparent; seeing out is fine unless
//                  containToUnion is set (AOE rays: the blast exists only
//                  inside the union, so an admitted crossing to the void is a
//                  stop, exactly like movement).
// Faces only exist across their box's vertical span — a ray above a 15 ft
// hall's ceiling crosses no wall of that hall (it is in the void; membership
// checks handle the void, not walls).
export function segmentHit(shape, x1, y1, x2, y2, { z = 0.5, mode = 'sight', containToUnion = false } = {}) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const segmentLength = Math.hypot(dx, dy);
  const openings = shape.openings ?? [];
  const stopAtUnionExit = mode === 'move' || containToUnion;
  let best = null;

  const consider = (axis, at, spanMin, spanMax, floorZ, ceilingZ) => {
    if (z < floorZ - EPS || z > ceilingZ + EPS) return;
    const [origin, delta] = axis === 'x' ? [x1, dx] : [y1, dy];
    if (Math.abs(delta) < EPS) return;
    const t = (at - origin) / delta;
    if (t <= EPS || t >= 1 - EPS) return;
    const crossX = x1 + t * dx;
    const crossY = y1 + t * dy;
    const spanCoord = axis === 'x' ? crossY : crossX;
    if (spanCoord < spanMin - EPS || spanCoord > spanMax + EPS) return;
    if (best && t >= best.t) return;
    // Overlap connects: a face crossing that lands strictly inside another box
    // (at this z) is interior to the union, not a wall.
    if (strictlyInsideAnyBox(shape, crossX, crossY, z)) return;
    const admitted = openings.some(opening => openingAdmits(opening, axis, at, spanCoord, z, mode));
    if (admitted) {
      if (stopAtUnionExit) {
        // World-space look-ahead (0.01 ft past the plane) — a parametric
        // epsilon would scale with segment length and could overjump a
        // narrow destination box on a long query segment.
        const aheadT = Math.min(1, t + 0.01 / Math.max(0.01, segmentLength));
        const aheadX = x1 + aheadT * dx;
        const aheadY = y1 + aheadT * dy;
        if (!insideUnion(shape, aheadX, aheadY)) best = { t, x: crossX, y: crossY, axis, at };
      }
      return;
    }
    best = { t, x: crossX, y: crossY, axis, at };
  };

  for (const box of shape.boxes) {
    if (boxIsNavigationOpenGround(box)) continue;
    consider('x', box.x.min, box.y.min, box.y.max, box.floor, box.ceiling);
    consider('x', box.x.max, box.y.min, box.y.max, box.floor, box.ceiling);
    consider('y', box.y.min, box.x.min, box.x.max, box.floor, box.ceiling);
    consider('y', box.y.max, box.x.min, box.x.max, box.floor, box.ceiling);
  }
  for (const face of sceneWallFaces(shape)) {
    if (z < face.floor - EPS || z > face.ceiling + EPS) continue;
    const [origin, delta] = face.axis === 'x' ? [x1, dx] : [y1, dy];
    if (Math.abs(delta) < EPS) continue;
    const t = (face.at - origin) / delta;
    if (t <= EPS || t >= 1 - EPS || (best && t >= best.t)) continue;
    const crossX = x1 + t * dx;
    const crossY = y1 + t * dy;
    const spanCoord = face.axis === 'x' ? crossY : crossX;
    if (spanCoord < face.min - EPS || spanCoord > face.max + EPS || sceneWallFaceAdmits(face, spanCoord, z)) continue;
    best = { t, x: crossX, y: crossY, axis: face.axis, at: face.at };
  }
  return best;
}

// Radial AOE clipped by walls: rays from the center stop at the first
// non-transparent face. Returns { points, area, fullArea } — points is the
// clip polygon (empty when the center sits outside the union).
export function clipRadialAoe(shape, cx, cy, radius, { z = 1, rayCount = 128 } = {}) {
  const fullArea = Math.PI * radius * radius;
  if (!insideUnion(shape, cx, cy)) return { points: [], area: 0, fullArea };
  const points = [];
  for (let index = 0; index < rayCount; index++) {
    const angle = (index / rayCount) * Math.PI * 2;
    const tx = cx + radius * Math.cos(angle);
    const ty = cy + radius * Math.sin(angle);
    // containToUnion: the blast exists only inside the union — a transparent
    // window must not let the polygon paint the void (keeps the render in
    // agreement with radialAoeContains).
    const hit = segmentHit(shape, cx, cy, tx, ty, { z, mode: 'sight', containToUnion: true });
    const reach = hit ? Math.max(0, hit.t - 1e-4) : 1;
    points.push({ x: cx + (tx - cx) * reach, y: cy + (ty - cy) * reach });
  }
  let area = 0;
  for (let index = 0; index < points.length; index++) {
    const a = points[index];
    const b = points[(index + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  return { points, area: Math.abs(area) / 2, fullArea };
}

// Point membership for a clipped radial AOE — recomputed directly (radius +
// clear sight line + inside the union), not read off the render polygon.
export function radialAoeContains(shape, cx, cy, radius, x, y, { z = 1 } = {}) {
  if (Math.hypot(x - cx, y - cy) > radius + EPS) return false;
  if (!insideUnion(shape, x, y)) return false;
  return segmentHit(shape, cx, cy, x, y, { z, mode: 'sight', containToUnion: true }) === null;
}

// SCENE WALLS (DAFT3D S24, owner 2026-09-22: walls are runs owned by a room OR by the scene). A wall
// the GM draws in the scene editor is saved as `shape.sceneWalls[]` and is a SOLID SLAB: two long faces
// at +-thickness/2 from its line and two end caps across its own z range. It is never excused by
// standing inside a room, and only its own openings admit a crossing.
// Twin: ft_shape_scene_wall_faces() in includes/functions/janus/encounter/ft3d_shape_core.php.
export function sceneWallFaces(shape) {
  const faces = [];
  for (const wall of shape.sceneWalls ?? []) {
    if (!wall || (wall.axis !== 'x' && wall.axis !== 'y')) continue;
    const other = wall.axis === 'x' ? 'y' : 'x';
    const at = Number(wall.at ?? 0);
    const half = Math.max(0.05, Number(wall.thicknessFt ?? 0.5)) / 2;
    const min = Number(wall.span?.min ?? 0);
    const max = Number(wall.span?.max ?? 0);
    const floor = Number(wall.z?.min ?? 0);
    const ceiling = Number(wall.z?.max ?? 0);
    if (!(max > min) || !(ceiling > floor)) continue;
    const openings = (wall.openings ?? []).filter(Boolean).map(opening => ({
      min: Number(opening.span?.min ?? 0), max: Number(opening.span?.max ?? 0),
      zmin: Number(opening.z?.min ?? floor), zmax: Number(opening.z?.max ?? ceiling),
    }));
    for (const plane of [at - half, at + half]) faces.push({ axis: wall.axis, at: plane, min, max, floor, ceiling, openings });
    for (const cap of [min, max]) faces.push({ axis: other, at: cap, min: at - half, max: at + half, floor, ceiling, openings: [] });
  }
  return faces;
}

function sceneWallFaceAdmits(face, spanCoord, z) {
  return face.openings.some(opening => spanCoord > opening.min + EPS && spanCoord < opening.max - EPS
    && z >= opening.zmin - EPS && z <= opening.zmax + EPS);
}

// First structural-wall hit for a true 3D segment. Unlike segmentHit(), the
// vertical coordinate is interpolated at each crossed face. Directional cover
// needs this because a source above a window can see different target samples
// than a source standing on the floor.
export function segmentHit3d(
  shape,
  source,
  target,
  { mode = 'sight', containToUnion = false } = {},
) {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const dz = target.z - source.z;
  const segmentLength = Math.hypot(dx, dy);
  const openings = shape.openings ?? [];
  const stopAtUnionExit = mode === 'move' || containToUnion;
  let best = null;

  const consider = (axis, at, spanMin, spanMax, floorZ, ceilingZ) => {
    const [origin, delta] = axis === 'x' ? [source.x, dx] : [source.y, dy];
    if (Math.abs(delta) < EPS) return;
    const t = (at - origin) / delta;
    if (t <= EPS || t >= 1 - EPS) return;
    const crossX = source.x + t * dx;
    const crossY = source.y + t * dy;
    const crossZ = source.z + t * dz;
    const spanCoord = axis === 'x' ? crossY : crossX;
    if (spanCoord < spanMin - EPS || spanCoord > spanMax + EPS) return;
    if (crossZ < floorZ - EPS || crossZ > ceilingZ + EPS) return;
    if (best && t >= best.t) return;
    if (strictlyInsideAnyBox(shape, crossX, crossY, crossZ)) return;
    const admitted = openings.some(opening => (
      openingAdmits(opening, axis, at, spanCoord, crossZ, mode)
    ));
    if (admitted) {
      if (stopAtUnionExit) {
        const aheadT = Math.min(1, t + 0.01 / Math.max(0.01, segmentLength));
        const aheadX = source.x + aheadT * dx;
        const aheadY = source.y + aheadT * dy;
        if (!insideUnion(shape, aheadX, aheadY)) {
          best = { t, x: crossX, y: crossY, z: crossZ, axis, at };
        }
      }
      return;
    }
    best = { t, x: crossX, y: crossY, z: crossZ, axis, at };
  };

  for (const box of shape.boxes) {
    if (boxIsNavigationOpenGround(box)) continue;
    consider('x', box.x.min, box.y.min, box.y.max, box.floor, box.ceiling);
    consider('x', box.x.max, box.y.min, box.y.max, box.floor, box.ceiling);
    consider('y', box.y.min, box.x.min, box.x.max, box.floor, box.ceiling);
    consider('y', box.y.max, box.x.min, box.x.max, box.floor, box.ceiling);
  }
  for (const face of sceneWallFaces(shape)) {
    const [origin, delta] = face.axis === 'x' ? [source.x, dx] : [source.y, dy];
    if (Math.abs(delta) < EPS) continue;
    const t = (face.at - origin) / delta;
    if (t <= EPS || t >= 1 - EPS || (best && t >= best.t)) continue;
    const crossX = source.x + t * dx;
    const crossY = source.y + t * dy;
    const crossZ = source.z + t * dz;
    const spanCoord = face.axis === 'x' ? crossY : crossX;
    if (spanCoord < face.min - EPS || spanCoord > face.max + EPS
      || crossZ < face.floor - EPS || crossZ > face.ceiling + EPS || sceneWallFaceAdmits(face, spanCoord, crossZ)) continue;
    best = { t, x: crossX, y: crossY, z: crossZ, axis: face.axis, at: face.at };
  }
  return best;
}

function segmentIntervalForAxis(origin, delta, min, max) {
  if (Math.abs(delta) < EPS) return origin >= min - EPS && origin <= max + EPS ? [-Infinity, Infinity] : null;
  const a = (min - origin) / delta;
  const b = (max - origin) / delta;
  return [Math.min(a, b), Math.max(a, b)];
}

function intersectIntervals(intervals) {
  let enter = -Infinity;
  let exit = Infinity;
  for (const interval of intervals) {
    if (!interval) return null;
    enter = Math.max(enter, interval[0]);
    exit = Math.min(exit, interval[1]);
  }
  return exit >= enter - EPS ? [enter, exit] : null;
}

// Abstract-volume intersection for the same size-tier cylinders / authored
// oblong bounds movement uses. Visual triangles are deliberately irrelevant.
export function segmentIntersectsCoverVolume(source, target, volume) {
  // Trusted runtime volumes may supply an exact intersection kernel (for example
  // a curved, sloping wall). Numeric bounds remain available to other consumers.
  if (typeof volume.intersectsSegment === 'function') return volume.intersectsSegment(source, target);
  const dz = target.z - source.z;
  const zInterval = segmentIntervalForAxis(source.z, dz, volume.zMin, volume.zMax);
  if (!zInterval) return false;

  let horizontal = null;
  if (volume.kind === 'circle') {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const ox = source.x - volume.x;
    const oy = source.y - volume.y;
    const a = dx * dx + dy * dy;
    if (a < EPS) return false;
    const b = 2 * (ox * dx + oy * dy);
    const c = ox * ox + oy * oy - volume.r * volume.r;
    const discriminant = b * b - 4 * a * c;
    if (discriminant < -EPS) return false;
    const root = Math.sqrt(Math.max(0, discriminant));
    horizontal = [(-b - root) / (2 * a), (-b + root) / (2 * a)];
  } else if (volume.kind === 'oblong') {
    const c = Math.cos(-volume.facing);
    const s = Math.sin(-volume.facing);
    const toLocal = point => {
      const x = point.x - volume.x;
      const y = point.y - volume.y;
      return { x: x * c - y * s, y: x * s + y * c };
    };
    const localSource = toLocal(source);
    const localTarget = toLocal(target);
    horizontal = intersectIntervals([
      segmentIntervalForAxis(localSource.x, localTarget.x - localSource.x, -volume.halfW, volume.halfW),
      segmentIntervalForAxis(localSource.y, localTarget.y - localSource.y, -volume.halfD, volume.halfD),
    ]);
  }
  if (!horizontal) return false;
  const hit = intersectIntervals([horizontal, zInterval, [EPS, 1 - EPS]]);
  return hit !== null;
}

export function directionalCoverSamples(source, targetVolume, { hunkered = false } = {}) {
  if (targetVolume.coverSamplePoints) return targetVolume.coverSamplePoints.map(point => ({ ...point }));
  const dx = targetVolume.x - source.x;
  const dy = targetVolume.y - source.y;
  const length = Math.hypot(dx, dy);
  const perpendicular = length > EPS ? { x: -dy / length, y: dx / length } : { x: 1, y: 0 };
  const lateral = targetVolume.kind === 'oblong'
    ? Math.max(targetVolume.halfW, targetVolume.halfD) * 0.72
    : targetVolume.r * 0.72;
  const zMin = targetVolume.zMin;
  const fullHeight = Math.max(EPS, targetVolume.zMax - zMin);
  const exposedHeight = fullHeight * (hunkered ? 0.62 : 1);
  const levels = [0.2, 0.5, 0.8];
  const offsets = [-lateral, 0, lateral];
  return levels.flatMap(level => offsets.map(offset => ({
    x: targetVolume.x + perpendicular.x * offset,
    y: targetVolume.y + perpendicular.y * offset,
    z: zMin + exposedHeight * level,
  })));
}

// Flanking angle: degrees at the TARGET between the caster and an ally, XY plane.
// 180 = directly opposite sides, 90 = a right angle, 0 = same side. Null when a
// leg is degenerate (a body sharing the target's centre). Transient attack-time
// evidence only — flanking is never a creature status. PHP port:
// ft_shape_flanking_angle_deg (parity via the golden vectors).
export function flankingAngleDeg(caster, target, ally) {
  const cx = caster.x - target.x;
  const cy = caster.y - target.y;
  const ax = ally.x - target.x;
  const ay = ally.y - target.y;
  const cl = Math.hypot(cx, cy);
  const al = Math.hypot(ax, ay);
  if (cl <= EPS || al <= EPS) return null;
  const cos = Math.max(-1, Math.min(1, (cx * ax + cy * ay) / (cl * al)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// Production directional-cover classifier. Each sample is tested against the
// encounter's structural walls and public abstract prop volumes. The result is
// a relationship for this source/target ray only; it is never a creature status.
export function directionalCoverBetween(
  shape,
  source,
  targetVolume,
  occluders = [],
  { hunkered = false, partialDefenseDelta = 2, heavyDefenseDelta = 4 } = {},
) {
  const samples = directionalCoverSamples(source, targetVolume, { hunkered });
  let structuralBlockedSamples = 0;
  let volumeBlockedSamples = 0;
  const blocked = samples.map(sample => {
    if (segmentHit3d(shape, source, sample, { mode: 'sight', containToUnion: true })) {
      structuralBlockedSamples += 1;
      return true;
    }
    if (occluders.some(volume => segmentIntersectsCoverVolume(source, sample, volume))) {
      volumeBlockedSamples += 1;
      return true;
    }
    return false;
  });
  const blockedSamples = blocked.filter(Boolean).length;
  const totalSamples = samples.length;
  let tier = 'none';
  if (blockedSamples >= 2) tier = 'partial';
  if (blockedSamples >= 6) tier = 'heavy';
  if (blockedSamples === totalSamples) tier = 'blocked';
  const effectiveDefenseDelta = tier === 'partial'
    ? partialDefenseDelta
    : tier === 'heavy' ? heavyDefenseDelta : 0;
  return {
    tier,
    blockedSamples,
    totalSamples,
    effectiveDefenseDelta,
    hunkerApplied: hunkered && blockedSamples > 0,
    structuralBlockedSamples,
    volumeBlockedSamples,
  };
}

// Direction-based cover: Xanathar-style corner sampling against the wall
// occluder set. Lab heuristic pending the owner's flanking/cover rules call —
// corners blocked 0 = none, 1-2 = half, 3 = three-quarters, 4 = total when the
// center is blocked too (else three-quarters).
export function coverBetween(shape, attacker, target, { z = 3, halfFt = 2.5 } = {}) {
  const inset = halfFt * 0.9;
  const corners = [
    { x: target.x - inset, y: target.y - inset },
    { x: target.x + inset, y: target.y - inset },
    { x: target.x - inset, y: target.y + inset },
    { x: target.x + inset, y: target.y + inset },
  ];
  const cornerBlocked = corners.map(corner =>
    segmentHit(shape, attacker.x, attacker.y, corner.x, corner.y, { z, mode: 'sight' }) !== null);
  const centerBlocked = segmentHit(shape, attacker.x, attacker.y, target.x, target.y, { z, mode: 'sight' }) !== null;
  const blockedCount = cornerBlocked.filter(Boolean).length;
  let tier = 'none';
  if (blockedCount >= 1) tier = 'half';
  if (blockedCount >= 3) tier = 'three-quarters';
  if (blockedCount === 4 && centerBlocked) tier = 'total';
  return { tier, blockedCount, centerBlocked, cornerBlocked };
}

// Wall render rectangles: each box face minus its openings, as
// { axis, at, inward, spanMin, spanMax, zMin, zMax, boxId } — presentation
// helper shared so the lab draws exactly the faces the rules enforce.
//
// v1 LIMITATION: openings are subtracted but OVERLAPPING box volumes are not —
// two overlapping boxes would render an interior wall the rules ignore
// (segmentHit's overlap exemption). Fine while content only ABUTS boxes (the
// declared-openings model); overlap-aware face subtraction must land before
// anyone authors overlapping boxes.
export function wallRects(shape) {
  const openings = shape.openings ?? [];
  const rects = [];
  const subtract = (list, hole) => list.flatMap(rect => {
    const spanMin = Math.max(rect.spanMin, hole.span.min);
    const spanMax = Math.min(rect.spanMax, hole.span.max);
    const zMin = Math.max(rect.zMin, hole.z.min);
    const zMax = Math.min(rect.zMax, hole.z.max);
    if (spanMin >= spanMax - EPS || zMin >= zMax - EPS) return [rect];
    const pieces = [];
    if (rect.spanMin < spanMin - EPS) pieces.push({ ...rect, spanMax: spanMin });
    if (spanMax < rect.spanMax - EPS) pieces.push({ ...rect, spanMin: spanMax });
    if (rect.zMin < zMin - EPS) pieces.push({ ...rect, spanMin, spanMax, zMax: zMin });
    if (zMax < rect.zMax - EPS) pieces.push({ ...rect, spanMin, spanMax, zMin: zMax });
    return pieces;
  });

  for (const box of shape.boxes) {
    if (boxIsNavigationOpenGround(box)) continue;
    const faces = [
      { axis: 'x', at: box.x.min, inward: 1, spanMin: box.y.min, spanMax: box.y.max },
      { axis: 'x', at: box.x.max, inward: -1, spanMin: box.y.min, spanMax: box.y.max },
      { axis: 'y', at: box.y.min, inward: 1, spanMin: box.x.min, spanMax: box.x.max },
      { axis: 'y', at: box.y.max, inward: -1, spanMin: box.x.min, spanMax: box.x.max },
    ];
    for (const face of faces) {
      let pieces = [{ ...face, zMin: box.floor, zMax: box.ceiling, boxId: box.id }];
      for (const opening of openings) {
        if (opening.plane.axis !== face.axis || Math.abs(opening.plane.at - face.at) > EPS) continue;
        pieces = subtract(pieces, opening);
      }
      rects.push(...pieces);
    }
  }
  return rects;
}

// ---------------------------------------------------------------------------
// Declarative room light fixtures (v1, 2026-08-28) — "DA has a checkbox to allow
// torches to hang from the sides". A box whose `creation.lightFixtures === 'sconces'`
// and which is structurally INTERIOR (creation present; preset / environment not
// exterior-open) gets wall sconces at deterministic positions derived ONLY from the
// blob, so the server (PHP port) and the client agree without persisting positions.
// Fixtures are architecture: always lit, not lootable, not interactive (v1).
//
// Spec (all lengths in hundredths of a foot as integers — no float drift):
//   walls in canonical order south (y=min, along x) → east (x=max, along y) →
//   north (y=max, along x) → west (x=min, along y); corner clearance 2.5 ft;
//   spacing 10 ft; candidates centred on the wall: n = floor((L − 5) / 10) + 1
//   when L ≥ 5, start = min + (L − (n−1)·10) / 2; a candidate within 2.5 ft
//   (inclusive) of an opening span on that wall plane is skipped; fixtures sit
//   0.5 ft inside the room and 5 ft above the floor (clamped to ceiling − 1);
//   at most 8 per room, chosen evenly over the canonical list; ids are
//   `<boxId>:<wall>:<index>`. Algorithm version LIGHT_FIXTURES_VERSION.
// ---------------------------------------------------------------------------
export const LIGHT_FIXTURES_VERSION = 1;
export const LIGHT_FIXTURE_MODES = Object.freeze(['none', 'sconces']);
const FIXTURE_SPACING_H = 1000;   // 10 ft
const FIXTURE_CLEARANCE_H = 250;  // 2.5 ft
const FIXTURE_INSET_H = 50;       // 0.5 ft
const FIXTURE_HEIGHT_H = 500;     // 5 ft above the floor
const FIXTURE_MAX_PER_ROOM = 8;
// Half-up rounding (JS Math.round semantics); the PHP port mirrors it with floor(v*100 + 0.5).
const toH = value => Math.round(value * 100);
const fromH = value => value / 100;

function boxIsStructurallyInterior(box) {
  const creation = box.creation && typeof box.creation === 'object' ? box.creation : null;
  if (!creation) return false;
  const preset = String(box.presetId ?? creation.presetId ?? '').trim().toLowerCase();
  if (preset === 'exterior-open') return false;
  if (preset !== '' && preset !== 'legacy-structural') return true;
  const explicit = box.environment ?? creation.environment ?? null;
  return explicit === 'interior';
}

/**
 * ═══ DAYLIGHT THROUGH AN APERTURE ══════════════════════════════════════════════════════════
 *
 * ⭐ Owner 2026-09-08: "the primary function of windows is actually to be a light source. When it's
 * bright outside, it creates dim light inside as the light passes through the window" — and the same
 * for a barred door, because permeability is what lets light through.
 *
 * ⚠ THIS LIVES ON THE CLIENT ON PURPOSE. The owner is moving light-type resolution into the Janus
 * FRONT-END push logic so the backend does not have to compute it, and the client already receives
 * the whole shape: `boxes` carry `enclosure` / `roofRemoved`, and every opening carries
 * `transparent`. Nothing extra has to be sent. `ft_shape_ambient_light_at()` in
 * includes/functions/janus/encounter/ft3d_shape_core.php is the mirror of this function and its
 * contract (tools/test/daft3d-structure-ladder-contract.php); the two must agree.
 *
 * ⚠ LIGHT AND SIGHT ARE TWO AXES, NOT ONE. The first cut read light off `transparent`, the SIGHT
 * field. Stained glass disproves that: the owner (2026-09-08) — "for glazed or stained glass ... I
 * don't know if the visibility should go through, but it should be maybe opaque from a vision
 * standpoint if the character wants to go up and look through the window. But I think light should
 * be able to pass through it." So an opening declares them SEPARATELY:
 *
 *   transparent  — can you SEE through it (line of sight, targeting)
 *   passesLight  — does DAYLIGHT come through it
 *
 * `passesLight` defaults to `transparent` when unstated, so every existing opening behaves exactly
 * as before: clear glass and bars pass both, a shut shutter passes neither, and only the stained or
 * frosted pane needs to say the two differ. It CAPS AT DIM however fierce the sun, and magical
 * darkness is never lifted.
 */
export const LIGHT_BRIGHT = '0CC8A592-CF4C-7BE4-DD0C-17A41D690A05';
export const LIGHT_DIM = '61D1498F-9175-6906-4B26-8356C28B28C1';
export const LIGHT_PITCH_BLACK = '99D3E371-AC3F-7839-28E7-770FE50D24C1';
export const LIGHT_MAGICAL_DARKNESS = '67F42B5B-E3D1-0818-0197-1F36B20FC630';

/** How far past an opening daylight spills. Beyond this the interior is on its own. */
export const APERTURE_LIGHT_REACH_FT = 10;

function lightRank(light) {
  if (light === LIGHT_BRIGHT) return 2;
  if (light === LIGHT_DIM) return 1;
  return 0;
}

/**
 * Does daylight come through this opening? Defaults to whether you can see through it, so only a
 * pane that differs — stained, frosted, a shuttered slit — has to say so.
 */
export function openingPassesLight(opening) {
  if (opening?.coveringState?.blocksLight === true) return false;
  if (opening?.passesLight !== undefined) return opening.passesLight === true;
  return opening?.transparent !== false;
}

/** Sealed from the sky: walls AND a ceiling that is still on. */
export function boxIsRoofed(box) {
  const creation = box?.creation && typeof box.creation === 'object' ? box.creation : {};
  return String(creation.enclosure ?? '').includes('ceiling') && creation.roofRemoved !== true;
}

/**
 * ═══ FACING (DAFT3D S26, owner D-S26-1 2026-09-25) ═════════════════════════════════════════
 *
 * An aperture is EXTERIOR when the far side of its wall is not another walled room, and INTERIOR
 * when it looks into one. Facing is a POSITION, never an identity: there is one `window` item type,
 * and the same window is interior or exterior only by where it stands. The compiler stamps it as
 * `creation.facing` on every window opening on every compile (the D-S11-11 latch-side discipline:
 * derived, never edited); a reader that meets an opening without the stamp — a door, a breach, a
 * shape saved before S26 — derives it from the same geometry below, so there is ONE rule.
 *
 * The generator uses the span form (`boxFaceSharedSpans`) to sweep only the exterior stretches of a
 * face; the light and perception readers use the opening form (`openingFacing`). Both read here.
 */

/** Environments with no outside to look out on. ONE list: the window generator and perception both read it. */
export const UNDERGROUND_ENVIRONMENTS = Object.freeze([
  '38FB8933-47F6-4A79-60CF-14E80BCF9BF5',
  '19BCE18F-E1DC-D359-48A0-3B789741E058',
]);

export function environmentIsUnderground(environmentId) {
  return UNDERGROUND_ENVIRONMENTS.includes(String(environmentId ?? '').trim().toUpperCase());
}

/**
 * Does this box stand as a walled room another room's wall can look into? Open ground, a room whose
 * Walls switch is off and a terrain pad are not: a window onto them is still a window onto the air.
 * Accepts a stored shape box (fields under `creation`) or a creation-draft room (fields on top).
 */
export function boxIsWalledRoom(box) {
  if (!box || box.navigationOpenGround === true) return false;
  const creation = box.creation && typeof box.creation === 'object' ? box.creation : {};
  const enclosure = creation.enclosure ?? box.enclosure;
  const presetId = creation.presetId ?? box.presetId;
  const environment = creation.environment ?? box.environment;
  return enclosure !== 'none' && presetId !== 'terrain-pad' && presetId !== 'exterior-open'
    && environment !== 'exterior-open';
}

function mergedSpans(spans) {
  const sorted = spans.filter(span => span.max - span.min > EPS).sort((a, b) => a.min - b.min);
  const out = [];
  for (const span of sorted) {
    const last = out[out.length - 1];
    if (last && span.min <= last.max + EPS) last.max = Math.max(last.max, span.max);
    else out.push({ min: span.min, max: span.max });
  }
  return out;
}

/**
 * The stretches of one face of `box` (the plane `axis = at`) that another walled room abuts from
 * the FAR side, within the vertical band `zBand`. A neighbour on a different floor shares nothing
 * unless its walls actually stand across that band, so a cellar under the room never makes a ground
 * floor wall "interior".
 *
 * @return {Array<{min:number,max:number}>} merged, sorted spans along the face
 */
export function boxFaceSharedSpans(box, boxes, axis, at, zBand = null) {
  if ((axis !== 'x' && axis !== 'y') || !box?.[axis]) return [];
  const along = axis === 'x' ? 'y' : 'x';
  const outward = Math.abs(at - box[axis].max) < EPS ? 1 : Math.abs(at - box[axis].min) < EPS ? -1 : 0;
  if (!outward) return [];
  const band = zBand ?? { min: Number(box.floor), max: Number(box.ceiling) };
  const spans = [];
  for (const other of boxes ?? []) {
    if (!other?.[axis] || other === box || String(other.id ?? '') === String(box.id ?? '')) continue;
    if (!boxIsWalledRoom(other)) continue;
    const near = outward > 0 ? other[axis].min : other[axis].max;
    if (Math.abs(near - at) > EPS) continue;
    if (!(Number(other.floor) < band.max - EPS && Number(other.ceiling) > band.min + EPS)) continue;
    spans.push({ min: Math.max(box[along].min, other[along].min), max: Math.min(box[along].max, other[along].max) });
  }
  return mergedSpans(spans);
}

/**
 * 'exterior' | 'interior' from geometry alone. An opening two rooms share (a doorway) is interior by
 * definition. Otherwise the opening's own room — the first of `creation.rooms` — decides the near
 * side, and the far side is interior when a walled room abuts it at the opening's centre and height.
 * An opening that names no room it stands in is interior only when walled rooms stand on BOTH sides.
 */
export function openingFacingFromGeometry(boxes, opening) {
  const axis = opening?.plane?.axis;
  if ((axis !== 'x' && axis !== 'y') || !opening?.span || !opening?.z) return 'exterior';
  const rooms = Array.isArray(opening.creation?.rooms) ? opening.creation.rooms.map(String) : [];
  if (rooms.length >= 2) return 'interior';
  const at = Number(opening.plane.at);
  const centre = (Number(opening.span.min) + Number(opening.span.max)) / 2;
  const along = axis === 'x' ? 'y' : 'x';
  const zBand = { min: Number(opening.z.min), max: Number(opening.z.max) };
  // Which sides of the plane a walled room stands on at this point.
  const sides = new Set();
  for (const box of boxes ?? []) {
    if (!box?.[axis] || !boxIsWalledRoom(box)) continue;
    if (!(centre > box[along].min + EPS && centre < box[along].max - EPS)) continue;
    if (!(Number(box.floor) < zBand.max - EPS && Number(box.ceiling) > zBand.min + EPS)) continue;
    if (Math.abs(box[axis].min - at) < EPS) sides.add(1);
    if (Math.abs(box[axis].max - at) < EPS) sides.add(-1);
  }
  const owner = rooms.length === 1 ? (boxes ?? []).find(box => String(box?.id ?? '') === rooms[0]) : null;
  if (owner?.[axis]) {
    const ownerSide = Math.abs(owner[axis].min - at) < EPS ? 1 : Math.abs(owner[axis].max - at) < EPS ? -1 : 0;
    if (ownerSide) return sides.has(-ownerSide) ? 'interior' : 'exterior';
  }
  return sides.has(1) && sides.has(-1) ? 'interior' : 'exterior';
}

/** The stamped facing when the compiler wrote one, else the same geometry the compiler uses. */
export function openingFacing(shape, opening) {
  const stamped = opening?.creation?.facing;
  if (stamped === 'exterior' || stamped === 'interior') return stamped;
  return openingFacingFromGeometry(shape?.boxes ?? [], opening);
}

/** Is this point close enough to the opening for daylight to reach it? */
export function pointWithinApertureReach(opening, x, y) {
  const axis = opening?.plane?.axis;
  if (axis !== 'x' && axis !== 'y') return false;
  const at = Number(opening.plane.at);
  const spanMin = Number(opening.span.min);
  const spanMax = Number(opening.span.max);
  // Distance THROUGH the wall and ALONG it: the spill is a rectangle in front of the opening, so a
  // wide window lights a wide strip and a slit lights a slit.
  const through = axis === 'x' ? Math.abs(x - at) : Math.abs(y - at);
  const along = axis === 'x' ? y : x;
  return through <= APERTURE_LIGHT_REACH_FT + 1e-6
    && along >= spanMin - APERTURE_LIGHT_REACH_FT - 1e-6
    && along <= spanMax + APERTURE_LIGHT_REACH_FT + 1e-6;
}

/**
 * The AMBIENT light at a point, before any torch or spell: the outdoor light where the sky reaches,
 * darkness where it does not, and dim near whatever openings let light through.
 *
 * A shape with no boxes returns the base light unchanged — that is every encounter that has no
 * shape, and they must behave exactly as they always did.
 */
export function ambientLightAt(shape, baseLight, x, y, { relay = true, inside: pinned = null } = {}) {
  if (baseLight === LIGHT_MAGICAL_DARKNESS) return baseLight;   // an effect, not a dimmer setting
  const boxes = shape?.boxes ?? [];
  if (!boxes.length) return baseLight;
  // A caller that already knows the room (perception, which selects by elevation band) pins it: the
  // 2D lookup below must never pick a different floor at the same x/y. The whole shape still rides
  // along, because FACING and the relay need the neighbours.
  const inside = pinned ?? boxes.find(box => box?.x && box?.y
    && x >= box.x.min - 1e-6 && x <= box.x.max + 1e-6
    && y >= box.y.min - 1e-6 && y <= box.y.max + 1e-6) ?? null;
  if (!inside || !boxIsRoofed(inside)) return baseLight;   // outdoors, or under an open sky
  const ambient = LIGHT_PITCH_BLACK;
  if (lightRank(baseLight) <= lightRank(ambient)) return ambient;   // no brighter out than in
  const roomId = String(inside.id ?? '');
  for (const opening of shape.openings ?? []) {
    if (!opening || !openingPassesLight(opening)) continue;   // this one passes no light
    const rooms = (opening.creation?.rooms ?? []).map(String);
    // Its own room's opening — or, for an INTERIOR window, the neighbour it looks into: a window
    // names only the room it was cut from, but both rooms stand at it (D-S26-1 ruling 3).
    if (rooms.length && !rooms.includes(roomId) && !openingOnBoxFace(opening, inside)) continue;
    if (!pointWithinApertureReach(opening, x, y)) continue;
    // ⭐ DAYLIGHT ENTERS ONLY THROUGH AN EXTERIOR-FACING APERTURE (D-S26-1 ruling 6). An interior
    // window between two roofed rooms used to fabricate dim daylight on both sides of it. It now
    // RELAYS its far side one hop — dim if that side is at least dim by its own lights, never
    // recursively, never brighter than dim. Doors and breaches follow the same geometry.
    if (openingFacing(shape, opening) === 'exterior') return LIGHT_DIM;   // capped at dim, always
    if (!relay) continue;
    const far = apertureFarSidePoint(opening, inside);
    if (!far) continue;
    // The room on the far side at THIS opening's height; none means the far side is open air.
    const farBox = boxes.find(box => box !== inside && box?.x && box?.y
      && far.x >= box.x.min - 1e-6 && far.x <= box.x.max + 1e-6
      && far.y >= box.y.min - 1e-6 && far.y <= box.y.max + 1e-6
      && Number(box.floor) < Number(opening.z.max) - EPS && Number(box.ceiling) > Number(opening.z.min) + EPS) ?? null;
    const farLight = farBox ? ambientLightAt(shape, baseLight, far.x, far.y, { relay: false, inside: farBox }) : baseLight;
    if (lightRank(farLight) >= lightRank(LIGHT_DIM)) return LIGHT_DIM;
  }
  return ambient;
}

/** Is this opening cut through one of this box's own faces (its plane, its centre, its height)? */
export function openingOnBoxFace(opening, box) {
  const axis = opening?.plane?.axis;
  if ((axis !== 'x' && axis !== 'y') || !box?.[axis] || !opening?.span || !opening?.z) return false;
  const along = axis === 'x' ? 'y' : 'x';
  const at = Number(opening.plane.at);
  const centre = (Number(opening.span.min) + Number(opening.span.max)) / 2;
  return (Math.abs(box[axis].min - at) < EPS || Math.abs(box[axis].max - at) < EPS)
    && centre > box[along].min + EPS && centre < box[along].max - EPS
    && Number(opening.z.min) < Number(box.ceiling) - EPS && Number(opening.z.max) > Number(box.floor) + EPS;
}

/** A point half a foot through the opening, on the side AWAY from the box the light is wanted in. */
function apertureFarSidePoint(opening, box) {
  const axis = opening.plane.axis;
  const at = Number(opening.plane.at);
  const out = Math.abs(box[axis].max - at) < EPS ? 1 : Math.abs(box[axis].min - at) < EPS ? -1 : 0;
  if (!out) return null;
  const centre = (Number(opening.span.min) + Number(opening.span.max)) / 2;
  return axis === 'x' ? { x: at + out * 0.5, y: centre } : { x: centre, y: at + out * 0.5 };
}

export function shapeLightFixtures(shape) {
  const out = [];
  const openings = shape.openings ?? [];
  for (const box of shape.boxes) {
    const creation = box.creation && typeof box.creation === 'object' ? box.creation : null;
    if (!creation || creation.lightFixtures !== 'sconces' || !boxIsStructurallyInterior(box)) continue;
    const xMin = toH(box.x.min); const xMax = toH(box.x.max);
    const yMin = toH(box.y.min); const yMax = toH(box.y.max);
    const floorH = toH(box.floor);
    const zH = Math.min(floorH + FIXTURE_HEIGHT_H, toH(box.ceiling) - 100);
    if (zH <= floorH) continue;
    // [name, axis of the wall PLANE, plane at, span min, span max, inward sign on the plane axis]
    const walls = [
      ['south', 'y', yMin, xMin, xMax, +1],
      ['east', 'x', xMax, yMin, yMax, -1],
      ['north', 'y', yMax, xMin, xMax, -1],
      ['west', 'x', xMin, yMin, yMax, +1],
    ];
    const candidates = [];
    for (const [name, axis, at, spanMin, spanMax, inward] of walls) {
      const length = spanMax - spanMin;
      if (length < 2 * FIXTURE_CLEARANCE_H) continue;
      const n = Math.floor((length - 2 * FIXTURE_CLEARANCE_H) / FIXTURE_SPACING_H) + 1;
      const start = spanMin + Math.floor((length - (n - 1) * FIXTURE_SPACING_H) / 2);
      let index = 0;
      for (let i = 0; i < n; i += 1) {
        const pos = start + i * FIXTURE_SPACING_H;
        // Only openings cut into THIS box's face count: same plane, span inside this wall, vertical span
        // overlapping this box (an opening on another story sharing the world plane must not block).
        const blocked = openings.some(opening => (
          opening.plane.axis === axis
          && toH(opening.plane.at) === at
          && toH(opening.span.min) >= spanMin && toH(opening.span.max) <= spanMax
          && toH(opening.z.max) > floorH && toH(opening.z.min) < toH(box.ceiling)
          && toH(opening.span.min) - FIXTURE_CLEARANCE_H <= pos
          && toH(opening.span.max) + FIXTURE_CLEARANCE_H >= pos
        ));
        if (blocked) continue;
        const along = fromH(pos);
        const off = fromH(at + inward * FIXTURE_INSET_H);
        candidates.push({
          id: `${box.id}:${name}:${index}`,
          boxId: box.id,
          wall: name,
          x: axis === 'x' ? off : along,
          y: axis === 'x' ? along : off,
          z: fromH(zH),
        });
        index += 1;
      }
    }
    if (candidates.length > FIXTURE_MAX_PER_ROOM) {
      const picked = [];
      for (let i = 0; i < FIXTURE_MAX_PER_ROOM; i += 1) {
        picked.push(candidates[Math.round(i * (candidates.length - 1) / (FIXTURE_MAX_PER_ROOM - 1))]);
      }
      out.push(...picked);
    } else {
      out.push(...candidates);
    }
  }
  return out;
}
